/**
 * Descriptions Component - Theme-aware key-value pairs display
 * ✅ Playbook Compliance: Theme-aware, WCAG 2.1 AA compliant, React.createElement
 */

const React = (globalThis as any).React;

import { styles } from '../styles';

export interface DescriptionsItemProps {
  label: React.ReactNode;
  children?: React.ReactNode;
  span?: number; // 1-24 (for grid layout)
  style?: React.CSSProperties;
}

export interface DescriptionsProps {
  title?: React.ReactNode;
  bordered?: boolean;
  column?: number; // Default: 3
  size?: 'small' | 'default' | 'large';
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

export function DescriptionsItem({ label, children, span = 1, style }: DescriptionsItemProps) {
  const itemStyle: React.CSSProperties = {
    gridColumn: `span ${span}`,
    ...style,
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: '600',
    color: 'var(--main-text-secondary)',
    padding: '8px 12px',
    background: 'var(--input-bg)',
    borderRight: '1px solid var(--border-color)',
  };

  const valueStyle: React.CSSProperties = {
    padding: '8px 12px',
    color: 'var(--main-text)',
    borderRight: '1px solid var(--border-color)',
  };

  return React.createElement('div', { style: itemStyle },
    React.createElement('div', { style: labelStyle }, label),
    React.createElement('div', { style: valueStyle }, children)
  );
}

export function Descriptions({
  title,
  bordered = false,
  column = 3,
  size = 'default',
  children,
  style,
}: DescriptionsProps) {
  const sizePadding = {
    small: '4px',
    default: '8px',
    large: '12px',
  }[size];

  const containerStyle: React.CSSProperties = {
    background: 'var(--card-bg)',
    border: bordered ? '1px solid var(--card-border)' : 'none',
    borderRadius: bordered ? '8px' : '0',
    overflow: 'hidden',
    ...style,
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${column}, 1fr)`,
    border: bordered ? '1px solid var(--border-color)' : 'none',
  };

  return React.createElement('div', { style: containerStyle },
    title && React.createElement('div', {
      style: {
        padding: sizePadding,
        borderBottom: bordered ? '1px solid var(--border-color)' : 'none',
        fontWeight: '600',
        color: 'var(--main-text)',
      }
    }, title),
    React.createElement('div', { style: gridStyle }, children)
  );
}

// Attach Item to Descriptions for convenience
(Descriptions as any).Item = DescriptionsItem;

